import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type {
  CompleteOnboarding,
  CreateCheckoutSession,
  CreateProCrew,
  LoadConfig,
  LoadMyEntitlements,
  LoadUserProfile,
  SendCrewInvite,
  SendOtp,
  SetSession,
  ClaimContact,
  SendContactClaimOtp,
  UpdateMyProfile,
  UploadProfilePhoto,
  ValidatePromoCode,
  VerifyOtp,
} from '@/modules/auth/use-cases'
import type {
  LoadCurrentDocument,
  RecordLegalAcceptance,
} from '@/modules/legal/use-cases'
import type { EmitFunnelEvent } from '@/shared/domain'
import { isProCrewsEnabled } from '@/shared/config'
import { isInFlutterWebview, useAuth, useForceTheme } from '@/shared/ui/hooks'
import {
  CREATE_CREW_FLOW,
  REGISTER_OTP_STATE_STORAGE_KEY,
  REGISTER_PROGRESS_STORAGE_KEY,
  STEP_TO_PHASE,
  type BillingCycle,
  type HunterProfileData,
  type OrgFormData,
  type OrgType,
  type RegisterPhase,
  type RegisterStep,
} from '../types'
import { useCrewBranch } from './use-crew-branch'

export type UseRegisterPageProps = {
  onLoadUserProfile: LoadUserProfile
  onSetSession: SetSession
  onUpdateMyProfile: UpdateMyProfile
  onCompleteOnboarding: CompleteOnboarding
  onSendContactClaimOtp: SendContactClaimOtp
  onClaimContact: ClaimContact
  onRecordLegalAcceptance: RecordLegalAcceptance
  onLoadCurrentDocument: LoadCurrentDocument
  onUploadProfilePhoto: UploadProfilePhoto
  onValidatePromoCode: ValidatePromoCode
  onCreateCheckoutSession: CreateCheckoutSession
  onLoadConfig: LoadConfig
  onLoadMyEntitlements: LoadMyEntitlements
  onCreateProCrew: CreateProCrew
  onSendCrewInvite: SendCrewInvite
  onSendOtp: SendOtp
  onVerifyOtp: VerifyOtp
  onEmitFunnelEvent: EmitFunnelEvent
  getAccessToken: () => Promise<string | null>
}

export type RegisterWizardState = {
  step: RegisterStep
  profileSkipped: boolean
  profileCompleted: boolean
  orgType: OrgType
  billingCycle: BillingCycle
  orgFormData: OrgFormData
  hunterProfileData: HunterProfileData
}

export type TrialDaysByOrgType = {
  club: number | null
  outfitter: number | null
  crew: number | null
}

export type UseRegisterPageResult = {
  isBootstrapping: boolean
  isCheckingProfile: boolean
  isMobile: boolean
  currentStep: RegisterStep
  currentPhase: RegisterPhase
  canGoBack: boolean
  canGoBackToPersona: boolean
  isSignOutModalOpen: boolean
  hunterProfileData: HunterProfileData
  hunterProfileOnboardingStep: number
  isProfileLocked: boolean
  sessionEmail: string | null
  orgType: OrgType
  billingCycle: BillingCycle
  orgFormData: OrgFormData
  trialDaysByOrgType: TrialDaysByOrgType
  showCrewCard: boolean
  isResolvingCrewGate: boolean
  crewMessage: string
  isCrewContinuing: boolean
  handleCrewContinue: () => Promise<void>
  handleCrewProRequired: () => void
  handleCrewCreated: () => void
  onCreateProCrew: CreateProCrew
  onSendCrewInvite: SendCrewInvite
  goToStep: (step: RegisterStep) => void
  goBack: () => void
  goNext: () => void
  handleHunterProfileSuccess: () => void
  handleRoleSelect: (orgType: OrgType) => void
  setOrgType: (orgType: OrgType) => void
  setBillingCycle: (billingCycle: BillingCycle) => void
  setOrgFormData: (data: OrgFormData) => void
  setHunterProfileData: (data: HunterProfileData) => void
  openSignOutModal: () => void
  closeSignOutModal: () => void
  confirmSignOut: () => Promise<void>
  onUpdateMyProfile: UpdateMyProfile
  onCompleteOnboarding: CompleteOnboarding
  onSendContactClaimOtp: SendContactClaimOtp
  onClaimContact: ClaimContact
  onRecordLegalAcceptance: RecordLegalAcceptance
  onLoadCurrentDocument: LoadCurrentDocument
  onUploadProfilePhoto: (file: File) => Promise<string>
  onValidatePromoCode: ValidatePromoCode
  onCreateCheckoutSession: CreateCheckoutSession
  onSendOtp: SendOtp
  onVerifyOtp: VerifyOtp
  onEmitFunnelEvent: EmitFunnelEvent
  getAccessToken: () => Promise<string | null>
}

const EMPTY_HUNTER_PROFILE: HunterProfileData = {
  firstName: '',
  lastName: '',
  contactValue: '',
  phoneE164: null,
  flywayRegion: '',
  profilePhotoUrl: '',
}

const EMPTY_ORG_FORM: OrgFormData = {
  orgName: '',
  state: '',
  country: 'US',
  promoCode: '',
}

const LEGACY_WIZARD_STEPS: RegisterStep[] = ['featurePage', 'orgForm', 'checkout']

function sanitizeSavedProgress(progress: Partial<RegisterWizardState>): Partial<RegisterWizardState> {
  if (progress.orgType === 'crew' && LEGACY_WIZARD_STEPS.includes(progress.step as RegisterStep)) {
    return { ...progress, step: 'roleSelection' }
  }
  if (progress.step === 'crewExplainer' || progress.step === 'crewName') {
    if (!isProCrewsEnabled() || !isInFlutterWebview()) return { ...progress, step: 'roleSelection' }
    return { ...progress, step: 'crewExplainer' }
  }
  return progress
}

function readSavedProgress(): Partial<RegisterWizardState> | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(REGISTER_PROGRESS_STORAGE_KEY)
    if (!raw) return null
    return sanitizeSavedProgress(JSON.parse(raw) as Partial<RegisterWizardState>)
  } catch {
    return null
  }
}

function isValidStep(value: unknown): value is RegisterStep {
  return (
    value === 'auth' ||
    value === 'hunterProfile' ||
    value === 'roleSelection' ||
    value === 'featurePage' ||
    value === 'orgForm' ||
    value === 'checkout' ||
    value === 'crewExplainer' ||
    value === 'crewName'
  )
}

function previousStep(step: RegisterStep, enteredDirectly: boolean): RegisterStep | null {
  if (step === 'crewExplainer') return enteredDirectly ? null : 'roleSelection'
  if (step === 'crewName') return 'crewExplainer'
  if (step === 'featurePage') return 'roleSelection'
  if (step === 'orgForm') return 'featurePage'
  if (step === 'checkout') return 'orgForm'
  return null
}

function nextStep(step: RegisterStep): RegisterStep | null {
  if (step === 'auth') return 'hunterProfile'
  if (step === 'hunterProfile') return 'roleSelection'
  if (step === 'roleSelection') return 'featurePage'
  if (step === 'featurePage') return 'orgForm'
  if (step === 'orgForm') return 'checkout'
  return null
}

export function useRegisterPage({
  onLoadUserProfile,
  onSetSession,
  onUpdateMyProfile,
  onCompleteOnboarding,
  onSendContactClaimOtp,
  onClaimContact,
  onRecordLegalAcceptance,
  onLoadCurrentDocument,
  onUploadProfilePhoto,
  onValidatePromoCode,
  onCreateCheckoutSession,
  onLoadConfig,
  onLoadMyEntitlements,
  onCreateProCrew,
  onSendCrewInvite,
  onSendOtp,
  onVerifyOtp,
  onEmitFunnelEvent,
  getAccessToken,
}: UseRegisterPageProps): UseRegisterPageResult {
  useForceTheme('dark')
  const { user, isLoading: isAuthLoading, signOut } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [saved] = useState<Partial<RegisterWizardState> | null>(readSavedProgress)

  const createCrewFlowRef = useRef<boolean>(searchParams.get('flow') === CREATE_CREW_FLOW)
  const [enteredCrewDirectly, setEnteredCrewDirectly] = useState<boolean>(
    () =>
      createCrewFlowRef.current &&
      (saved?.step === 'crewExplainer' || saved?.step === 'crewName'),
  )
  const [crewCreated, setCrewCreated] = useState<boolean>(false)

  const isMobileSourceRef = useRef<boolean>(
    isInFlutterWebview() || Boolean(searchParams.get('access_token')),
  )

  const [step, setStep] = useState<RegisterStep>(() => {
    const initial = saved?.step
    return isValidStep(initial) ? initial : 'auth'
  })
  const [profileSkipped, setProfileSkipped] = useState<boolean>(saved?.profileSkipped ?? false)
  const [profileCompleted, setProfileCompleted] = useState<boolean>(
    saved?.profileCompleted ?? false,
  )
  const [orgType, setOrgType] = useState<OrgType>(saved?.orgType ?? 'club')
  const [billingCycle, setBillingCycle] = useState<BillingCycle>(saved?.billingCycle ?? 'monthly')
  const [orgFormData, setOrgFormData] = useState<OrgFormData>(saved?.orgFormData ?? EMPTY_ORG_FORM)
  const [hunterProfileData, setHunterProfileData] = useState<HunterProfileData>(
    saved?.hunterProfileData ?? EMPTY_HUNTER_PROFILE,
  )

  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    const params = new URLSearchParams(window.location.search)
    return Boolean(params.get('access_token') && params.get('refresh_token'))
  })
  const [isCheckingProfile, setIsCheckingProfile] = useState<boolean>(false)
  const [isSignOutModalOpen, setIsSignOutModalOpen] = useState<boolean>(false)

  const bootstrapRef = useRef<boolean>(false)
  const profileCheckRef = useRef<boolean>(false)
  const configLoadedRef = useRef<boolean>(false)
  const [trialDaysByOrgType, setTrialDaysByOrgType] = useState<TrialDaysByOrgType>({
    club: null,
    outfitter: null,
    crew: null,
  })

  useEffect(() => {
    if (configLoadedRef.current) return
    configLoadedRef.current = true
    onLoadConfig()
      .then((config) => {
        const perOrgType = config.trialDurationDaysByOrgType
        const fallback =
          typeof config.trialDurationDays === 'number' ? config.trialDurationDays : null
        setTrialDaysByOrgType({
          club: perOrgType?.club ?? fallback,
          outfitter: perOrgType?.outfitter ?? fallback,
          crew: perOrgType?.crew ?? fallback,
        })
      })
      .catch(() => {})
  }, [onLoadConfig])

  useEffect(() => {
    if (bootstrapRef.current) return
    const accessToken = searchParams.get('access_token')
    const refreshToken = searchParams.get('refresh_token')
    if (!accessToken || !refreshToken) {
      setIsBootstrapping(false)
      return
    }
    bootstrapRef.current = true
    setIsBootstrapping(true)
    onSetSession({ accessToken, refreshToken })
      .catch(() => {})
      .finally(() => {
        if (typeof window !== 'undefined') {
          window.history.replaceState({}, '', '/register')
        }
        setIsBootstrapping(false)
      })
  }, [searchParams, onSetSession])

  useEffect(() => {
    if (isAuthLoading || isBootstrapping) return
    if (user) return
    if (step === 'auth') return
    setStep('auth')
    profileCheckRef.current = false
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(REGISTER_PROGRESS_STORAGE_KEY)
    }
  }, [isAuthLoading, isBootstrapping, user, step])

  useEffect(() => {
    if (isAuthLoading || isBootstrapping) return
    if (!user) return
    if (profileCheckRef.current) return
    profileCheckRef.current = true

    const hasSavedProgress = isValidStep(saved?.step) && saved?.step !== 'auth'
    if (!hasSavedProgress) setIsCheckingProfile(true)
    onLoadUserProfile()
      .then((result) => {
        const hasName = Boolean(result.user.firstName && result.user.lastName)
        if (hasName) {
          setProfileSkipped(true)
          setProfileCompleted(true)
          setHunterProfileData((current) =>
            current.firstName !== '' || current.lastName !== ''
              ? current
              : {
                  firstName: result.user.firstName ?? '',
                  lastName: result.user.lastName ?? '',
                  contactValue: result.user.email,
                  phoneE164: result.user.phone ?? null,
                  flywayRegion: result.user.flywayRegion ?? '',
                  profilePhotoUrl: '',
                },
          )
          if (!hasSavedProgress) setStep('roleSelection')
        } else if (!hasSavedProgress) {
          setStep((current) => (current === 'auth' ? 'hunterProfile' : current))
        }
      })
      .catch(() => {
        if (!hasSavedProgress) {
          setStep((current) => (current === 'auth' ? 'hunterProfile' : current))
        }
      })
      .finally(() => {
        setIsCheckingProfile(false)
      })
  }, [isAuthLoading, isBootstrapping, user, onLoadUserProfile, saved])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (step === 'auth' || crewCreated) return
    const snapshot: RegisterWizardState = {
      step,
      profileSkipped,
      profileCompleted,
      orgType,
      billingCycle,
      orgFormData,
      hunterProfileData,
    }
    window.sessionStorage.setItem(REGISTER_PROGRESS_STORAGE_KEY, JSON.stringify(snapshot))
  }, [step, crewCreated, profileSkipped, profileCompleted, orgType, billingCycle, orgFormData, hunterProfileData])

  const stepRef = useRef<RegisterStep>(step)
  const registrationStartedRef = useRef<boolean>(false)
  const emitRef = useRef<EmitFunnelEvent>(onEmitFunnelEvent)
  emitRef.current = onEmitFunnelEvent

  useEffect(() => {
    stepRef.current = step
    if (step === 'auth') return
    if (!registrationStartedRef.current) {
      registrationStartedRef.current = true
      emitRef.current({ eventType: 'registration_started' })
    }
    emitRef.current({ eventType: 'registration_step_viewed', step })
  }, [step])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const handleBeforeUnload = () => {
      if (stepRef.current === 'auth') return
      emitRef.current({ eventType: 'registration_abandoned', step: stepRef.current })
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  const crewGateOpen = isProCrewsEnabled() && isInFlutterWebview()
  const crewFlowPending =
    createCrewFlowRef.current &&
    !enteredCrewDirectly &&
    crewGateOpen &&
    step === 'roleSelection'
  const isResolvingCrewGate = step === 'roleSelection' && crewFlowPending

  useEffect(() => {
    if (!crewFlowPending) return
    setEnteredCrewDirectly(true)
    setOrgType('crew')
    setStep('crewExplainer')
  }, [crewFlowPending])

  useEffect(() => {
    if (crewGateOpen) return
    setStep((current) =>
      current === 'crewExplainer' || current === 'crewName' ? 'roleSelection' : current,
    )
  }, [crewGateOpen])

  const advanceCrewToName = useCallback(() => {
    emitRef.current({ eventType: 'registration_step_completed', step: 'crewExplainer' })
    setStep('crewName')
  }, [])

  const crewBranch = useCrewBranch({
    active: step === 'crewExplainer',
    onLoadMyEntitlements,
    onAdvance: advanceCrewToName,
  })
  const { showProRequired } = crewBranch

  const handleCrewProRequired = useCallback(() => {
    showProRequired()
    setStep('crewExplainer')
  }, [showProRequired])

  const handleCrewCreated = useCallback(() => {
    setCrewCreated(true)
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(REGISTER_PROGRESS_STORAGE_KEY)
    }
  }, [])

  const currentPhase = STEP_TO_PHASE[step]
  const canGoBack = !crewCreated && previousStep(step, enteredCrewDirectly) !== null
  const canGoBackToPersona =
    !crewCreated &&
    (step === 'featurePage' ||
      step === 'orgForm' ||
      step === 'checkout' ||
      step === 'crewName' ||
      (step === 'crewExplainer' && !enteredCrewDirectly))

  const goToStep = useCallback((next: RegisterStep) => {
    setStep(next)
  }, [])

  const goBack = useCallback(() => {
    if (crewCreated) return
    setStep((current) => previousStep(current, enteredCrewDirectly) ?? current)
  }, [crewCreated, enteredCrewDirectly])

  const goNext = useCallback(() => {
    setStep((current) => {
      const next = nextStep(current) ?? current
      if (next !== current && current !== 'auth') {
        emitRef.current({ eventType: 'registration_step_completed', step: current })
      }
      return next
    })
  }, [])

  const handleHunterProfileSuccess = useCallback(() => {
    setProfileCompleted(true)
    setStep((current) => {
      const next = nextStep(current) ?? current
      if (next !== current && current !== 'auth') {
        emitRef.current({ eventType: 'registration_step_completed', step: current })
      }
      return next
    })
  }, [])

  const handleRoleSelect = useCallback(
    (selected: OrgType) => {
      if (selected === 'crew' && !crewGateOpen) return
      setOrgType(selected)
      setStep((current) => {
        const next = selected === 'crew' ? 'crewExplainer' : (nextStep(current) ?? current)
        if (next !== current && current !== 'auth') {
          emitRef.current({ eventType: 'registration_step_completed', step: current })
        }
        return next
      })
    },
    [crewGateOpen],
  )

  const openSignOutModal = useCallback(() => {
    setIsSignOutModalOpen(true)
  }, [])

  const closeSignOutModal = useCallback(() => {
    setIsSignOutModalOpen(false)
  }, [])

  const uploadProfilePhotoAdapter = useCallback(
    async (file: File) => (await onUploadProfilePhoto({ file })).url,
    [onUploadProfilePhoto],
  )

  const confirmSignOut = useCallback(async () => {
    setIsSignOutModalOpen(false)
    if (stepRef.current !== 'auth') {
      emitRef.current({ eventType: 'registration_abandoned', step: stepRef.current })
    }
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(REGISTER_PROGRESS_STORAGE_KEY)
      window.sessionStorage.removeItem(REGISTER_OTP_STATE_STORAGE_KEY)
    }
    if (user) {
      await signOut()
    }
    navigate('/')
  }, [navigate, signOut, user])

  const sessionEmail = user?.email?.trim() || null

  return useMemo(
    () => ({
      isBootstrapping,
      isCheckingProfile,
      isMobile: isMobileSourceRef.current,
      currentStep: step,
      currentPhase,
      canGoBack,
      canGoBackToPersona,
      isSignOutModalOpen,
      hunterProfileData,
      hunterProfileOnboardingStep: STEP_TO_PHASE.roleSelection,
      isProfileLocked: profileCompleted,
      sessionEmail,
      orgType,
      billingCycle,
      orgFormData,
      trialDaysByOrgType,
      showCrewCard: crewGateOpen,
      isResolvingCrewGate,
      crewMessage: crewBranch.message,
      isCrewContinuing: crewBranch.isContinuing,
      handleCrewContinue: crewBranch.handleContinue,
      handleCrewProRequired,
      handleCrewCreated,
      onCreateProCrew,
      onSendCrewInvite,
      goToStep,
      goBack,
      goNext,
      handleHunterProfileSuccess,
      handleRoleSelect,
      setOrgType,
      setBillingCycle,
      setOrgFormData,
      setHunterProfileData,
      openSignOutModal,
      closeSignOutModal,
      confirmSignOut,
      onUpdateMyProfile,
      onCompleteOnboarding,
      onSendContactClaimOtp,
      onClaimContact,
      onRecordLegalAcceptance,
      onLoadCurrentDocument,
      onUploadProfilePhoto: uploadProfilePhotoAdapter,
      onValidatePromoCode,
      onCreateCheckoutSession,
      onSendOtp,
      onVerifyOtp,
      onEmitFunnelEvent,
      getAccessToken,
    }),
    [
      isBootstrapping,
      isCheckingProfile,
      step,
      currentPhase,
      canGoBack,
      canGoBackToPersona,
      isSignOutModalOpen,
      hunterProfileData,
      profileCompleted,
      sessionEmail,
      orgType,
      billingCycle,
      orgFormData,
      trialDaysByOrgType,
      crewGateOpen,
      isResolvingCrewGate,
      crewBranch.message,
      crewBranch.isContinuing,
      crewBranch.handleContinue,
      handleCrewProRequired,
      handleCrewCreated,
      onCreateProCrew,
      onSendCrewInvite,
      goToStep,
      goBack,
      goNext,
      handleHunterProfileSuccess,
      handleRoleSelect,
      openSignOutModal,
      closeSignOutModal,
      confirmSignOut,
      onUpdateMyProfile,
      onCompleteOnboarding,
      onSendContactClaimOtp,
      onClaimContact,
      onRecordLegalAcceptance,
      onLoadCurrentDocument,
      uploadProfilePhotoAdapter,
      onValidatePromoCode,
      onCreateCheckoutSession,
      onSendOtp,
      onVerifyOtp,
      onEmitFunnelEvent,
      getAccessToken,
    ],
  )
}
